import React, { useState, useEffect } from 'react';
import { useContext } from 'react';
import { AuthContext } from '../../components/context/AuthContext';
import { useAds } from '../../components/context/AdsContext';
import { useChat } from '../../components/context/ChatContext';
import { GoogleSearchPreview, GooglePreview } from '../../components';
import InstagramFeedPreview from '../../components/chat/previews/InstagramFeedPreview';
import StoryPreview from '../../components/chat/previews/StoryPreview';
import mediaApi from '../../api/media';
import metaApi from '../../api/meta';

/**
 * Step 3: Ad Preview Component
 * 
 * This component renders the ad preview step of the campaign wizard.
 * It shows different preview types based on the selected campaign type:
 * - Google Search ads (traffic and conversions)
 * - Google Performance Max ads (with/without catalog)
 * - Meta ads (traffic, conversions, catalog)
 * - WhatsApp conversation ads
 * 
 * For Meta ads, it includes editing capabilities for headlines, descriptions, and media.
 * 
 * @param {Object} props - Component props
 * @param {string} props.campaignTypeObject - Selected campaign type
 * @param {Object} props.metaState - Meta ad editing state
 * @param {Function} props.onMetaStateChange - Callback to update meta state
 * @param {Function} props.onCampaignUpdate - Global callback to update campaign
 * @param {Object} props.currentCampaign - Campaign data from API
 * @param {Object} props.chatSession - Chat session data
 * @param {boolean} props.isEditingCampaign - Whether campaign is being edited
 * @param {string} props.editError - Error message from campaign editing
 * @returns {JSX.Element} Ad preview interface
 */
const Step3AdPreview = ({ campaignTypeObject, metaState, onMetaStateChange, onCampaignUpdate, currentCampaign, chatSession, isEditingCampaign, editError, onValidationChange }) => {
  // Estado para validación de Google Search
  const [isGoogleSearchValid, setIsGoogleSearchValid] = useState(true);
  const { auth } = useContext(AuthContext);
  const { activeMetaAccount } = useAds();
  const { fetchCampaign } = useChat();
  const userInfo = { business_name: auth?.user?.business_name || 'Tu Negocio' };
  
  // Get available catalogs from active Meta account
  const availableCatalogs = activeMetaAccount?.catalogs?.data || [];

  // Media upload state
  const [isUploadingMedia, setIsUploadingMedia] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [campaignMedia, setCampaignMedia] = useState([]);

  /**
   * Refetches the campaign data and updates the current campaign
   */
  const refetchCampaign = async () => {
    if (!chatSession) return;
    
    try {
      const updatedCampaign = await fetchCampaign(chatSession);
      if (updatedCampaign) {
        // Update the current campaign with the fresh data
        onCampaignUpdate(updatedCampaign);
      }
    } catch (error) {
      console.error('Error refetching campaign after upload:', error);
    }
  };

  // Catalog state
  const [catalogData, setCatalogData] = useState(null);
  const [isLoadingCatalog, setIsLoadingCatalog] = useState(false);
  const [catalogError, setCatalogError] = useState(null);
  const [currentProductIndex, setCurrentProductIndex] = useState(0);

  // Load existing media when campaign is available
  useEffect(() => {
    const loadCampaignMedia = async () => {
      if (!currentCampaign?.id) return;
      
      try {
        const mediaResponse = await mediaApi.getMedia(currentCampaign.id);
        if (mediaResponse.data) {
          // Check if data is an array before calling map
          const mediaData = Array.isArray(mediaResponse.data) ? mediaResponse.data : [];
          const mediaUrls = mediaData.map(media => media.url);
          setCampaignMedia(mediaUrls);
          
          // Always update metaState with existing media to ensure they're displayed
          if (mediaUrls.length > 0) {
            onMetaStateChange({ 
              images: mediaUrls,
              imageIndex: 0
            });
          }
        }
      } catch (error) {
        console.error('Error loading campaign media:', error);
        // Don't throw the error, just log it and continue
      }
    };

    loadCampaignMedia();
  }, [currentCampaign?.id, onMetaStateChange]);

  // Load catalog data when campaign is available and has catalog_id
  useEffect(() => {
    const loadCatalogData = async () => {
      // Check multiple locations for catalog ID
      const catalogId = currentCampaign?.meta_settings?.advantage_plus?.catalog_id || 
                       currentCampaign?.meta_settings?.promoted_object?.product_catalog_id;
      
      if (!catalogId) {
        setCatalogData(null);
        return;
      }

      try {
        setIsLoadingCatalog(true);
        setCatalogError(null);
        
        const response = await metaApi.getCatalog(catalogId);
        if (response.data) {
          setCatalogData(response.data);
          setCurrentProductIndex(0); // Reset to first product
          // Also update the campaign with catalog data
          onCampaignUpdate({
            catalog: response.data
          });
        } else {
          throw new Error('No catalog data received');
        }
      } catch (error) {
        console.error('Error loading catalog:', error);
        setCatalogError(error.message || 'Error al cargar el catálogo');
        setCatalogData(null);
      } finally {
        setIsLoadingCatalog(false);
      }
    };

    loadCatalogData();
  }, [currentCampaign?.meta_settings?.advantage_plus?.catalog_id, currentCampaign?.meta_settings?.promoted_object?.product_catalog_id, onCampaignUpdate]);

  // Also load catalog when campaign becomes available (for initial load)
  useEffect(() => {
    // Check multiple locations for catalog ID
    const catalogId = currentCampaign?.meta_settings?.advantage_plus?.catalog_id || 
                     currentCampaign?.meta_settings?.promoted_object?.product_catalog_id;
    
    // Only auto-load if we have a catalog ID and no catalog data is loaded yet
    if (catalogId && !catalogData && !isLoadingCatalog) {
      console.log('Auto-loading catalog on campaign load:', catalogId);
      const loadCatalogData = async () => {
        try {
          setIsLoadingCatalog(true);
          setCatalogError(null);
          
          const response = await metaApi.getCatalog(catalogId);
          if (response.data) {
            setCatalogData(response.data);
            setCurrentProductIndex(0);
            onCampaignUpdate({
              catalog: response.data
            });
            console.log('Catalog auto-loaded successfully:', response.data);
          } else {
            throw new Error('No catalog data received');
          }
        } catch (error) {
          console.error('Error auto-loading catalog:', error);
          setCatalogError(error.message || 'Error al cargar el catálogo');
          setCatalogData(null);
        } finally {
          setIsLoadingCatalog(false);
        }
      };
      
      loadCatalogData();
    }
  }, [currentCampaign?.id, currentCampaign?.meta_settings?.advantage_plus?.catalog_id, currentCampaign?.meta_settings?.promoted_object?.product_catalog_id, catalogData, isLoadingCatalog, onCampaignUpdate]);

  // Product navigation functions
  const nextProduct = () => {
    const allProducts = currentCampaign?.catalog?.products || catalogData?.products || [];
    setCurrentProductIndex(prev => prev < allProducts.length - 1 ? prev + 1 : 0);
  };

  const prevProduct = () => {
    const allProducts = currentCampaign?.catalog?.products || catalogData?.products || [];
    setCurrentProductIndex(prev => prev > 0 ? prev - 1 : allProducts.length - 1);
  };

  // Meta ad editing handlers
  const handleEditHeadline = async (idx, current) => { 
    await onMetaStateChange({ 
      editingHeadline: idx, 
      headlineDraft: metaState.title || current || '' 
    }); 
  };
  
  const handleEditDescription = async (idx, current) => { 
    await onMetaStateChange({ 
      editingDescription: idx, 
      descriptionDraft: metaState.description || current || '' 
    }); 
  };
  
  const handleSaveHeadline = async (value = null) => { 
    const headlineValue = value || metaState.headlineDraft;
    console.log('handleSaveHeadline called with value:', headlineValue);
    await onMetaStateChange({ 
      title: headlineValue, 
      editingHeadline: -1 
    }); 
    console.log('handleSaveHeadline completed');
  };
  
  const handleSaveDescription = async (value = null) => { 
    const descriptionValue = value || metaState.descriptionDraft;
    console.log('handleSaveDescription called with value:', descriptionValue);
    await onMetaStateChange({ 
      description: descriptionValue, 
      editingDescription: -1 
    }); 
    console.log('handleSaveDescription completed');
  };
  
  const handleCancelEdit = async () => { 
    await onMetaStateChange({ 
      editingHeadline: -1, 
      editingDescription: -1 
    }); 
  };

  const handleMetaImageSelection = () => metaState.fileInputRef?.current?.click();
  
  const handleMetaFileInputChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !currentCampaign?.id) return;
    
    try {
      setIsUploadingMedia(true);
      setUploadError(null);
      
      // Upload files to the campaign using mediaApi
      const uploadResponse = await mediaApi.uploadMedia(currentCampaign.id, files);
      
      if (uploadResponse.data) {
        // Extract campaign and media data from response
        const { campaign, media } = uploadResponse.data;
        
        // Get the uploaded media URLs from the media array
        const uploadedMediaUrls = media.map(mediaItem => mediaItem.url);
        
        // Update local campaign media state
        const updatedCampaignMedia = [...campaignMedia, ...uploadedMediaUrls];
        setCampaignMedia(updatedCampaignMedia);
        
        // Update the campaign's ad_content with new media
        const updatedImages = [...(metaState.images || []), ...uploadedMediaUrls];
        onMetaStateChange({ 
          images: updatedImages,
          imageIndex: updatedImages.length - 1
        });
        
        // Refetch the campaign to get the latest data from the server
        await refetchCampaign();
        
        console.log('Media uploaded successfully:', {
          campaign: campaign,
          media: media,
          uploadedUrls: uploadedMediaUrls
        });
      } else {
        throw new Error('Error al subir los archivos');
      }
    } catch (error) {
      console.error('Error uploading media:', error);
      setUploadError(error.message || 'Error al subir los archivos');
    } finally {
      setIsUploadingMedia(false);
    }
  };
  
  const removeCurrentMetaImage = () => {
    const currentImages = metaState.images || [];
    if (!currentImages.length) return;
    
    const updatedImages = currentImages.filter((_, i) => i !== metaState.imageIndex);
    onMetaStateChange({ 
      images: updatedImages,
      imageIndex: Math.max(0, metaState.imageIndex - 1)
    });
  };

  if (!campaignTypeObject) return null;

  // Show loading state if campaign is being created or loaded
  if (!currentCampaign) {
    return (
      <div className="bg-slate-800/50 rounded-2xl border border-white/20 p-5">
        <div className="flex items-center justify-center py-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-white/80">
              {chatSession ? 'Cargando campaña desde la sesión...' : 'Cargando anuncios de la campaña...'}
            </p>
            {chatSession && (
              <p className="text-xs text-white/60 mt-2">Session: {chatSession}</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Google Search Campaigns
  if (campaignTypeObject.key === 'GOOGLE_SEARCH_TRAFFIC') {
    // Extract data from current campaign using the real structure
    const adContent = currentCampaign?.ad_content || {};
    const googleSettings = currentCampaign?.google_settings || {};
    
    const handleGoogleSearchChange = async (changes) => {
      console.log('GoogleSearchPreview onChange called with:', changes);
      await onCampaignUpdate(changes);
    };

    const handleGoogleSearchValidationChange = (isValid) => {
      setIsGoogleSearchValid(isValid);
      // También notificar al componente padre si hay una función de validación
      if (onValidationChange) {
        onValidationChange(isValid);
      }
    };

    return (
      <div className="bg-slate-800/50 rounded-2xl border border-white/20 p-5">
        <GoogleSearchPreview
          title={adContent.headlines?.[0] || "Título del anuncio"}
          url={adContent.final_url || "https://tusitio.com"}
          description={adContent.descriptions?.[0] || "Descripción del anuncio"}
          keywords={googleSettings.keywords || ['palabra 1', 'palabra 2']}
          titles={adContent.headlines || ["Título del anuncio"]}
          descriptions={adContent.descriptions || ["Descripción del anuncio"]}
          onChange={handleGoogleSearchChange}
          onValidationChange={handleGoogleSearchValidationChange}
        />
        
        {/* Mensaje de validación para Google Search */}
        {!isGoogleSearchValid && (
          <div className="mt-4 p-3 bg-orange-900/50 border border-orange-700/50 rounded-lg">
            <div className="flex items-center gap-2">
              <div className="text-orange-400 text-lg">🙈</div>
              <div>
                <p className="text-sm text-orange-200">
                  <strong>Ups… ¡la IA se emocionó escribiendo!</strong> Editá tus textos para que entren en el límite.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Google Performance Max Campaigns
  if (campaignTypeObject.key === 'GOOGLE_PMAX_NO_CATALOG' || campaignTypeObject.key === 'GOOGLE_PMAX_WITH_CATALOG') {
    const isWithCatalog = campaignTypeObject.key === 'GOOGLE_PMAX_WITH_CATALOG';
    
    // Use real campaign data with the correct structure
    const adContent = currentCampaign?.ad_content || {};
    const googleSettings = currentCampaign?.google_settings || {};
    
    const previewData = {
      id: currentCampaign?.id, // Add campaign ID for media uploads
      campaign_type: 'PERFORMANCE_MAX',
      ad_content: {
        final_url: adContent.final_url || 'https://tusitio.com',
        headlines: adContent.headlines || ['Título del anuncio'],
        descriptions: adContent.descriptions || ['Descripción del anuncio']
      },
      google_settings: {
        keywords: googleSettings.keywords || ['palabra 1', 'palabra 2']
      }
    };
    
    const handleGooglePerformanceMaxChange = async (changes) => {
      console.log('GooglePerformanceMax onChange called with:', changes);
      await onCampaignUpdate(changes);
    };
    
    return (
      <div className="bg-slate-800/50 rounded-2xl border border-white/20 p-5 space-y-4">
        {isWithCatalog && (
          <div className="flex items-center gap-2 text-sm text-white/80 bg-green-900/30 border border-green-700/50 rounded-lg p-3">
            <span className="inline-flex w-2 h-2 rounded-full bg-green-500" />
            <span className="font-medium">Merchant Center habilitado</span>
            <span className="text-white/60">• Tus productos se van a mostrar en la red de shopping</span>
          </div>
        )}
        <GooglePreview
          campaignPreview={previewData}
          isCampaignSent={false}
          onValidationChange={onValidationChange}
          onChange={handleGooglePerformanceMaxChange}
          onRefetchCampaign={refetchCampaign}
        />
      </div>
    );
  }

  // Check if campaign has catalog data (regardless of campaign type)
  const hasCatalogData = Boolean(
    currentCampaign?.meta_settings?.advantage_plus?.catalog_id || 
    currentCampaign?.meta_settings?.promoted_object?.product_catalog_id ||
    currentCampaign?.catalog?.products?.length > 0 ||
    catalogData?.products?.length > 0
  );

  // Meta Campaigns (Traffic and Conversions) - but check for catalog first
  if ((campaignTypeObject.key === 'META_TRAFFIC' || campaignTypeObject.key === 'META_CONVERSIONS') && !hasCatalogData) {
    console.log('Rendering META_TRAFFIC/META_CONVERSIONS section (no catalog)');
    const isEditMode = true;
    
    // Extract Meta campaign data from the real structure
    const adContent = currentCampaign?.ad_content || {};
    // Prioritize metaState values (user edits) over campaign data
    const campaignTitle = metaState.title || adContent.headlines?.[0] || 'Título ejemplo';
    const campaignDescription = metaState.description || adContent.descriptions?.[0] || 'Descripción ejemplo';
    
    // Use metaState.images if available (includes uploaded images), otherwise use campaign images
    const campaignImages = (metaState.images && metaState.images.length > 0) 
      ? metaState.images 
      : (adContent.media?.images || []);
    
    return (
      <div className="bg-slate-800/50 rounded-2xl border border-white/20 p-5">
        {/* Campaign editing status */}
        {isEditingCampaign && (
          <div className="mb-4 flex items-center gap-2 p-3 bg-blue-900/30 border border-blue-700/50 rounded-lg">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-400"></div>
            <span className="text-sm text-blue-300">Guardando cambios...</span>
          </div>
        )}

        {editError && (
          <div className="mb-4 p-3 bg-red-900/50 border border-red-700/50 rounded-lg">
            <p className="text-sm text-red-300">
              <strong>Error:</strong> {editError}
            </p>
          </div>
        )}

        {/* Upload status messages */}
        {isUploadingMedia && (
          <div className="mb-4 p-3 bg-blue-900/30 border border-blue-700/50 rounded-lg">
            <p className="text-sm text-blue-300">Subiendo archivos...</p>
          </div>
        )}
        
        {uploadError && (
          <div className="mb-4 p-3 bg-red-900/50 border border-red-700/50 rounded-lg">
            <p className="text-sm text-red-300">{uploadError}</p>
          </div>
        )}
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 justify-items-center md:justify-items-stretch">
          <InstagramFeedPreview
            campaignPreview={{ business_name: userInfo.business_name }}
            user={userInfo}
            title={campaignTitle}
            description={campaignDescription}
            hasMedia={campaignImages.length > 0}
            mediaUrl={campaignImages[metaState.imageIndex || 0]}
            mediaInputRef={metaState.fileInputRef}
            handleImageSelection={handleMetaImageSelection}
            removeCurrentImage={removeCurrentMetaImage}
            likes={128}
            isEditMode={isEditMode}
            editingHeadline={metaState.editingHeadline !== undefined ? metaState.editingHeadline : -1}
            editingDescription={metaState.editingDescription !== undefined ? metaState.editingDescription : -1}
            editHeadlineText={metaState.headlineDraft || ''}
            editDescriptionText={metaState.descriptionDraft || ''}
            handleEditHeadline={handleEditHeadline}
            handleEditDescription={handleEditDescription}
            handleSaveHeadline={handleSaveHeadline}
            handleSaveDescription={handleSaveDescription}
            handleCancelEdit={handleCancelEdit}
            setEditHeadlineText={async (text) => await onMetaStateChange({ headlineDraft: text })}
            setEditDescriptionText={async (text) => await onMetaStateChange({ descriptionDraft: text })}
            isUploading={isUploadingMedia}
          />
          <StoryPreview
            campaignPreview={{ business_name: userInfo.business_name }}
            user={userInfo}
            hasMedia={campaignImages.length > 0}
            mediaUrl={campaignImages[metaState.imageIndex || 0]}
            mediaInputRef={metaState.fileInputRef}
            handleImageSelection={handleMetaImageSelection}
            removeCurrentImage={removeCurrentMetaImage}
            imageOrientation="portrait"
            isUploading={isUploadingMedia}
          />
        </div>

        {/* Shared dots slider */}
        {campaignImages.length > 1 && (
          <div className="mt-3 flex items-center justify-center gap-2">
            {campaignImages.map((_, i) => (
              <button 
                key={i} 
                onClick={() => onMetaStateChange({ imageIndex: i })} 
                className={`w-2.5 h-2.5 rounded-full ${i === (metaState.imageIndex || 0) ? 'bg-white' : 'bg-white/40'} transition-colors`} 
              />
            ))}
          </div>
        )}

        {/* Image requirement message */}
        {campaignImages.length === 0 && (
          <div className="mt-4 p-3 bg-yellow-900/30 border border-yellow-700/50 rounded-lg text-center">
            <p className="text-sm text-yellow-200">
              <strong>Importante:</strong> Necesitás subir al menos una imagen para continuar con la campaña.
            </p>
          </div>
        )}

        {/* Hidden file input */}
        <input 
          ref={metaState.fileInputRef} 
          type="file" 
          accept="image/*,video/*" 
          multiple 
          className="hidden" 
          onChange={handleMetaFileInputChange} 
        />
      </div>
    );
  }

  // Meta Campaigns WITH Catalog Data (regardless of campaign type)
  if ((campaignTypeObject.key === 'META_TRAFFIC' || campaignTypeObject.key === 'META_CONVERSIONS') && hasCatalogData) {
    console.log('Rendering META campaign WITH catalog data');
    // Check multiple locations for catalog ID
    const catalogId = currentCampaign?.meta_settings?.advantage_plus?.catalog_id || 
                     currentCampaign?.meta_settings?.promoted_object?.product_catalog_id;
    
    // Use catalog data from currentCampaign or from local state
    const catalogProducts = currentCampaign?.catalog?.products || catalogData?.products || [];
    const currentProduct = catalogProducts[currentProductIndex];
    
    // Extract Meta campaign data from the real structure for editing
    const adContent = currentCampaign?.ad_content || {};
    const campaignDescription = metaState.description || adContent.descriptions?.[0] || 'Descripción ejemplo';
    
    return (
      <div className="bg-slate-800/50 rounded-2xl border border-white/20 p-5">
        {/* Catalog Selection Section */}
        <div className="mb-6">
          
          {/* Catalog Selection Dropdown */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-white/80 mb-2">
              Seleccionar Catálogo de Meta
            </label>
            <div className="flex items-center gap-3">
              <select
                value={catalogId || ''}
                onChange={async (e) => {
                  const newCatalogId = e.target.value;
                  
                  // Update the campaign with new catalog ID
                  await onCampaignUpdate({
                    meta_settings: {
                      ...currentCampaign?.meta_settings,
                      advantage_plus: {
                        ...currentCampaign?.meta_settings?.advantage_plus,
                        catalog_id: newCatalogId
                      },
                      promoted_object: {
                        ...currentCampaign?.meta_settings?.promoted_object,
                        product_catalog_id: newCatalogId
                      }
                    }
                  });

                  // Auto-load catalog if ID is valid (not empty)
                  if (newCatalogId && newCatalogId.trim().length > 0) {
                    try {
                      setIsLoadingCatalog(true);
                      setCatalogError(null);
                      
                      console.log('Auto-loading catalog with ID:', newCatalogId);
                      const response = await metaApi.getCatalog(newCatalogId);
                      console.log('Catalog API response:', response);
                      
                      if (response.data) {
                        setCatalogData(response.data);
                        setCurrentProductIndex(0);
                        onCampaignUpdate({
                          catalog: response.data
                        });
                        console.log('Catalog auto-loaded successfully:', response.data);
                      } else {
                        throw new Error('No catalog data received');
                      }
                    } catch (error) {
                      console.error('Error auto-loading catalog:', error);
                      setCatalogError(error.message || 'Error al cargar el catálogo');
                      setCatalogData(null);
                    } finally {
                      setIsLoadingCatalog(false);
                    }
                  } else {
                    // Clear catalog data if ID is empty
                    setCatalogData(null);
                    setCatalogError(null);
                  }
                }}
                className="flex-1 border border-white/30 bg-slate-700/50 rounded-lg px-2 lg:px-3 py-2 text-xs lg:text-sm text-white focus:ring-2 focus:ring-primary focus:border-transparent outline-none truncate"
                style={{ maxWidth: '100%' }}
              >
                <option value="">Selecciona un catálogo</option>
                {availableCatalogs.map((catalog) => {
                  // Truncate catalog name for mobile
                  const displayName = catalog.name.length > 25 
                    ? `${catalog.name.substring(0, 25)}...` 
                    : catalog.name;
                  return (
                    <option key={catalog.id} value={catalog.id} title={catalog.name}>
                      {displayName} ({catalog.product_count} productos)
                    </option>
                  );
                })}
              </select>
            </div>
          </div>

          {/* Loading State */}
          {isLoadingCatalog && (
            <div className="flex items-center gap-2 p-3 bg-blue-900/30 border border-blue-700/50 rounded-lg">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-400"></div>
              <span className="text-sm text-blue-300">Cargando catálogo...</span>
            </div>
          )}

          {/* Error State */}
          {catalogError && (
            <div className="p-3 bg-red-900/50 border border-red-700/50 rounded-lg">
              <p className="text-sm text-red-300">
                <strong>Error:</strong> {catalogError}
              </p>
            </div>
          )}

          {/* No Catalog Selected */}
          {!catalogId && (
            <div className="p-4 bg-blue-900/30 border border-blue-700/50 rounded-lg">
              <p className="text-sm text-blue-300">
                <strong>Información:</strong> Ingresa un ID de catálogo de Meta para ver la vista previa de tus productos. El catálogo se cargará automáticamente.
              </p>
            </div>
          )}

        </div>

        {/* Instagram Feed and Story Previews with Catalog */}
        {catalogId && catalogProducts.length > 0 && (
          <div className="mt-6">
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Instagram Feed Preview with Catalog */}
              <div className="flex flex-col items-center">
                <InstagramFeedPreview
                  campaignPreview={{ business_name: userInfo.business_name }}
                  user={userInfo}
                  title={`${currentProduct?.name || 'Producto'} - ${currentProduct?.price || '$0.00'}`}
                  description={campaignDescription}
                  hasMedia={Boolean(currentProduct?.image_url)}
                  mediaUrl={currentProduct?.image_url}
                  hasCatalog={true}
                  likes={128}
                  isEditMode={true}
                  allowHeadlineEdit={false}
                  allowDescriptionEdit={true}
                  editingDescription={metaState.editingDescription !== undefined ? metaState.editingDescription : -1}
                  editDescriptionText={metaState.descriptionDraft || ''}
                  handleEditDescription={handleEditDescription}
                  handleSaveDescription={handleSaveDescription}
                  handleCancelEdit={handleCancelEdit}
                  setEditDescriptionText={async (text) => await onMetaStateChange({ descriptionDraft: text })}
                />
              </div>

              {/* Story Preview with Catalog */}
              <div className="flex flex-col items-center">
                <StoryPreview
                  campaignPreview={{ business_name: userInfo.business_name }}
                  user={userInfo}
                  hasMedia={Boolean(currentProduct?.image_url)}
                  mediaUrl={currentProduct?.image_url}
                  hasCatalog={true}
                  imageOrientation="portrait"
                />
              </div>
            </div>

            {/* Product Navigation */}
            {catalogProducts.length > 1 && (
              <div className="mt-4 flex items-center justify-center gap-4">
                <button
                  onClick={prevProduct}
                  className="w-8 h-8 bg-white/20 text-white rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
                  title="Producto anterior"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                
                <span className="text-sm text-white/80 font-medium">
                  Producto {currentProductIndex + 1} de {catalogProducts.length}
                </span>
                
                <button
                  onClick={nextProduct}
                  className="w-8 h-8 bg-white/20 text-white rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
                  title="Siguiente producto"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            )}

            {/* Product Dots Navigation */}
            {catalogProducts.length > 1 && catalogProducts.length <= 10 && (
              <div className="mt-2 flex justify-center gap-1">
                {catalogProducts.slice(0, 10).map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentProductIndex(index)}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      index === currentProductIndex 
                        ? 'bg-blue-400' 
                        : 'bg-white/40 hover:bg-white/60'
                    }`}
                    title={`Ir a producto ${index + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Fallback Preview for when no catalog is loaded */}
        {!catalogData && !isLoadingCatalog && !catalogError && catalogId && (
          <div className="border-t border-white/20 pt-6">
            <div className="p-4 bg-slate-700/50 border border-white/20 rounded-lg">
              <p className="text-sm text-white/80 text-center">
                Haz clic en "Cargar Catálogo" para ver la vista previa de tus productos
              </p>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Meta Catalog Campaign
  if (campaignTypeObject.key === 'META_CATALOG') {
    console.log('Rendering META_CATALOG section');
    // Check multiple locations for catalog ID
    const catalogId = currentCampaign?.meta_settings?.advantage_plus?.catalog_id || 
                     currentCampaign?.meta_settings?.promoted_object?.product_catalog_id;
    const hasCatalog = Boolean(catalogId);
    
    // Use catalog data from currentCampaign or from local state
    const catalogProducts = currentCampaign?.catalog?.products || catalogData?.products || [];
    const currentProduct = catalogProducts[currentProductIndex];
    
    // Extract Meta campaign data from the real structure for editing
    const adContent = currentCampaign?.ad_content || {};
    const campaignDescription = metaState.description || adContent.descriptions?.[0] || 'Descripción ejemplo';
    
    return (
      <div className="bg-slate-800/50 rounded-2xl border border-white/20 p-5">
        {/* Catalog Selection Section */}
        <div className="mb-6">
          
          {/* Catalog Selection Dropdown */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-white/80 mb-2">
              Seleccionar Catálogo de Meta
            </label>
            <div className="flex items-center gap-3">
              <select
                value={catalogId || ''}
                onChange={async (e) => {
                  const newCatalogId = e.target.value;
                  
                  // Update the campaign with new catalog ID
                  await onCampaignUpdate({
                    meta_settings: {
                      ...currentCampaign?.meta_settings,
                      advantage_plus: {
                        ...currentCampaign?.meta_settings?.advantage_plus,
                        catalog_id: newCatalogId
                      },
                      promoted_object: {
                        ...currentCampaign?.meta_settings?.promoted_object,
                        product_catalog_id: newCatalogId
                      }
                    }
                  });

                  // Auto-load catalog if ID is valid (not empty)
                  if (newCatalogId && newCatalogId.trim().length > 0) {
                    try {
                      setIsLoadingCatalog(true);
                      setCatalogError(null);
                      
                      console.log('Auto-loading catalog with ID:', newCatalogId);
                      const response = await metaApi.getCatalog(newCatalogId);
                      console.log('Catalog API response:', response);
                      
                      if (response.data) {
                        setCatalogData(response.data);
                        setCurrentProductIndex(0);
                        onCampaignUpdate({
                          catalog: response.data
                        });
                        console.log('Catalog auto-loaded successfully:', response.data);
                      } else {
                        throw new Error('No catalog data received');
                      }
                    } catch (error) {
                      console.error('Error auto-loading catalog:', error);
                      setCatalogError(error.message || 'Error al cargar el catálogo');
                      setCatalogData(null);
                    } finally {
                      setIsLoadingCatalog(false);
                    }
                  } else {
                    // Clear catalog data if ID is empty
                    setCatalogData(null);
                    setCatalogError(null);
                  }
                }}
                className="flex-1 border border-white/30 bg-slate-700/50 rounded-lg px-2 lg:px-3 py-2 text-xs lg:text-sm text-white focus:ring-2 focus:ring-primary focus:border-transparent outline-none truncate"
                style={{ maxWidth: '100%' }}
              >
                <option value="">Selecciona un catálogo</option>
                {availableCatalogs.map((catalog) => {
                  // Truncate catalog name for mobile
                  const displayName = catalog.name.length > 25 
                    ? `${catalog.name.substring(0, 25)}...` 
                    : catalog.name;
                  return (
                    <option key={catalog.id} value={catalog.id} title={catalog.name}>
                      {displayName} ({catalog.product_count} productos)
                    </option>
                  );
                })}
              </select>
            </div>
          </div>

          {/* Loading State */}
          {isLoadingCatalog && (
            <div className="flex items-center gap-2 p-3 bg-blue-900/30 border border-blue-700/50 rounded-lg">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-400"></div>
              <span className="text-sm text-blue-300">Cargando catálogo...</span>
            </div>
          )}

          {/* Error State */}
          {catalogError && (
            <div className="p-3 bg-red-900/50 border border-red-700/50 rounded-lg">
              <p className="text-sm text-red-300">
                <strong>Error:</strong> {catalogError}
              </p>
            </div>
          )}

          {/* No Catalog Selected */}
          {!catalogId && (
            <div className="p-4 bg-blue-900/30 border border-blue-700/50 rounded-lg">
              <p className="text-sm text-blue-300">
                <strong>Información:</strong> Ingresa un ID de catálogo de Meta para ver la vista previa de tus productos. El catálogo se cargará automáticamente.
              </p>
            </div>
          )}

          {/* Catalog Status */}
          {hasCatalog && catalogProducts.length > 0 && (
            <div className="p-3 bg-green-900/30 border border-green-700/50 rounded-lg">
              <p className="text-sm text-green-300">
                <strong>✓ Catálogo cargado:</strong> {catalogProducts.length} productos disponibles
              </p>
            </div>
          )}
        </div>

        {/* Instagram Feed and Story Previews with Catalog */}
        {hasCatalog && (catalogProducts.length > 0 || catalogData?.products?.length > 0) && (
          <div className="mt-6">
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Instagram Feed Preview with Catalog */}
              <div className="flex flex-col items-center">
                <InstagramFeedPreview
                  campaignPreview={{ business_name: userInfo.business_name }}
                  user={userInfo}
                  title={`${currentProduct?.name || 'Producto'} - ${currentProduct?.price || '$0.00'}`}
                  description={campaignDescription}
                  hasMedia={Boolean(currentProduct?.image_url)}
                  mediaUrl={currentProduct?.image_url}
                  hasCatalog={true}
                  likes={128}
                  isEditMode={true}
                  allowHeadlineEdit={false}
                  allowDescriptionEdit={true}
                  editingDescription={metaState.editingDescription !== undefined ? metaState.editingDescription : -1}
                  editDescriptionText={metaState.descriptionDraft || ''}
                  handleEditDescription={handleEditDescription}
                  handleSaveDescription={handleSaveDescription}
                  handleCancelEdit={handleCancelEdit}
                  setEditDescriptionText={async (text) => await onMetaStateChange({ descriptionDraft: text })}
                />
              </div>

              {/* Story Preview with Catalog */}
              <div className="flex flex-col items-center">
                <StoryPreview
                  campaignPreview={{ business_name: userInfo.business_name }}
                  user={userInfo}
                  hasMedia={Boolean(currentProduct?.image_url)}
                  mediaUrl={currentProduct?.image_url}
                  hasCatalog={true}
                  imageOrientation="portrait"
                />
              </div>
            </div>

            {/* Product Navigation */}
            {catalogProducts.length > 1 && (
              <div className="mt-4 flex items-center justify-center gap-4">
                <button
                  onClick={prevProduct}
                  className="w-8 h-8 bg-white/20 text-white rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
                  title="Producto anterior"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                
                <span className="text-sm text-white/80 font-medium">
                  Producto {currentProductIndex + 1} de {catalogProducts.length}
                </span>
                
                <button
                  onClick={nextProduct}
                  className="w-8 h-8 bg-white/20 text-white rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
                  title="Siguiente producto"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            )}

            {/* Product Dots Navigation */}
            {catalogProducts.length > 1 && catalogProducts.length <= 10 && (
              <div className="mt-2 flex justify-center gap-1">
                {catalogProducts.slice(0, 10).map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentProductIndex(index)}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      index === currentProductIndex 
                        ? 'bg-blue-400' 
                        : 'bg-white/40 hover:bg-white/60'
                    }`}
                    title={`Ir a producto ${index + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Fallback Preview for when no catalog is loaded */}
        {!catalogData && !isLoadingCatalog && !catalogError && catalogId && (
          <div className="border-t border-white/20 pt-6">
            <div className="p-4 bg-slate-700/50 border border-white/20 rounded-lg">
              <p className="text-sm text-white/80 text-center">
                Haz clic en "Cargar Catálogo" para ver la vista previa de tus productos
              </p>
            </div>
          </div>
        )}
      </div>
    );
  }

  // WhatsApp Campaign - Use same preview as Meta Traffic/Conversions
  if (campaignTypeObject.key === 'WHATSAPP_CONVERSATIONS') {
    console.log('Rendering WHATSAPP_CONVERSATIONS section with Instagram Feed preview');
    const isEditMode = true;
    
    // Extract Meta campaign data from the real structure
    const adContent = currentCampaign?.ad_content || {};
    // Prioritize metaState values (user edits) over campaign data
    const campaignTitle = metaState.title || adContent.headlines?.[0] || 'Título ejemplo';
    const campaignDescription = metaState.description || adContent.descriptions?.[0] || 'Descripción ejemplo';
    
    // Use metaState.images if available (includes uploaded images), otherwise use campaign images
    const campaignImages = (metaState.images && metaState.images.length > 0) 
      ? metaState.images 
      : (adContent.media?.images || []);
    
    return (
      <div className="bg-slate-800/50 rounded-2xl border border-white/20 p-5">
        
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-white">Vista previa del anuncio</h3>
            <p className="text-sm text-white/80">Así se verá tu anuncio en Facebook e Instagram</p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 justify-items-center md:justify-items-stretch">
          <InstagramFeedPreview
            campaignPreview={{ business_name: userInfo.business_name }}
            user={userInfo}
            title={campaignTitle}
            description={campaignDescription}
            hasMedia={campaignImages.length > 0}
            mediaUrl={campaignImages[metaState.imageIndex || 0]}
            mediaInputRef={metaState.fileInputRef}
            handleImageSelection={handleMetaImageSelection}
            removeCurrentImage={removeCurrentMetaImage}
            likes={128}
            isEditMode={isEditMode}
            editingHeadline={metaState.editingHeadline !== undefined ? metaState.editingHeadline : -1}
            editingDescription={metaState.editingDescription !== undefined ? metaState.editingDescription : -1}
            editHeadlineText={metaState.headlineDraft || ''}
            editDescriptionText={metaState.descriptionDraft || ''}
            handleEditHeadline={handleEditHeadline}
            handleEditDescription={handleEditDescription}
            handleSaveHeadline={handleSaveHeadline}
            handleSaveDescription={handleSaveDescription}
            handleCancelEdit={handleCancelEdit}
            setEditHeadlineText={async (text) => await onMetaStateChange({ headlineDraft: text })}
            setEditDescriptionText={async (text) => await onMetaStateChange({ descriptionDraft: text })}
            isUploading={isUploadingMedia}
          />
          <StoryPreview
            campaignPreview={{ business_name: userInfo.business_name }}
            user={userInfo}
            hasMedia={campaignImages.length > 0}
            mediaUrl={campaignImages[metaState.imageIndex || 0]}
            mediaInputRef={metaState.fileInputRef}
            handleImageSelection={handleMetaImageSelection}
            removeCurrentImage={removeCurrentMetaImage}
            imageOrientation="portrait"
            isUploading={isUploadingMedia}
          />
        </div>

        {/* Shared dots slider */}
        {campaignImages.length > 1 && (
          <div className="mt-4 flex justify-center gap-1">
            {campaignImages.map((_, index) => (
              <button
                key={index}
                onClick={() => onMetaStateChange({ imageIndex: index })}
                className={`w-2 h-2 rounded-full transition-colors ${
                  index === (metaState.imageIndex || 0) 
                    ? 'bg-blue-400' 
                    : 'bg-white/40 hover:bg-white/60'
                }`}
                title={`Ir a imagen ${index + 1}`}
              />
            ))}
          </div>
        )}

        {/* Image requirement message */}
        {campaignImages.length === 0 && (
          <div className="mt-4 p-3 bg-yellow-900/30 border border-yellow-700/50 rounded-lg text-center">
            <p className="text-sm text-yellow-200">
              <strong>Importante:</strong> Necesitás subir al menos una imagen para continuar con la campaña.
            </p>
          </div>
        )}

        {/* Hidden file input */}
        <input 
          ref={metaState.fileInputRef} 
          type="file" 
          accept="image/*,video/*" 
          multiple 
          className="hidden" 
          onChange={handleMetaFileInputChange} 
        />
      </div>
    );
  }

  return null;
};

export default Step3AdPreview;
